<script lang="ts">
  import { sendMessage, ApiError } from "../lib/api";
  import { push } from "svelte-spa-router";

  export let label: string | undefined = undefined;

  let text = "";
  let submitting = false;
  let info: string | null = null;
  let error: string | null = null;
  // Target selector — both on by default (matches back-compat behaviour).
  let toKiosk = true;
  let toStudent = true;

  async function submit(e: Event) {
    e.preventDefault();
    info = null;
    error = null;
    if (!text.trim()) {
      error = "Message required.";
      return;
    }
    const targets: ("kiosk" | "student")[] = [];
    if (toKiosk) targets.push("kiosk");
    if (toStudent) targets.push("student");
    if (targets.length === 0) {
      error = "Pick at least one target.";
      return;
    }
    submitting = true;
    try {
      const res = await sendMessage({ text, targets }, label);
      info = `queued (id=${res.id})`;
      text = "";
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "send failed";
    } finally {
      submitting = false;
    }
  }
</script>

<section>
  <h1>Push message</h1>
  <p class="hint">
    Queues an <code>admin_msg</code> for the next agent heartbeat. The agent
    forwards to the kiosk via the localhost WS bridge.
  </p>
  <form on:submit={submit}>
    <label>
      Message
      <textarea
        rows="3"
        bind:value={text}
        data-testid="text"
        placeholder="Hello student, the teacher will be 5 min late."
        maxlength="2000"
      ></textarea>
    </label>
    <fieldset class="targets">
      <legend>Show on</legend>
      <label class="check">
        <input type="checkbox" bind:checked={toKiosk} data-testid="target-kiosk" />
        Kiosk / device
      </label>
      <label class="check">
        <input type="checkbox" bind:checked={toStudent} data-testid="target-student" />
        Student
      </label>
    </fieldset>
    <button
      type="submit"
      disabled={submitting || (!toKiosk && !toStudent)}
      data-testid="submit"
    >
      {submitting ? "Sending…" : "Send"}
    </button>
    {#if info}
      <p class="info" data-testid="info">{info}</p>
    {/if}
    {#if error}
      <p class="error" data-testid="error">{error}</p>
    {/if}
  </form>
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  .hint {
    color: #9ca3af;
    font-size: 0.85rem;
  }
  form {
    display: grid;
    gap: 0.6rem;
    max-width: 480px;
  }
  label {
    display: grid;
    gap: 0.2rem;
    color: #d1d5db;
  }
  textarea {
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.5rem;
    resize: vertical;
  }
  button {
    padding: 0.55rem 0.8rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 6px;
    justify-self: start;
  }
  .targets {
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    display: grid;
    gap: 0.3rem;
  }
  .targets legend {
    color: #9ca3af;
    font-size: 0.8rem;
    padding: 0 0.3rem;
  }
  .check {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
  }
  .check input {
    width: auto;
  }
  .info {
    color: #6ee7b7;
  }
  .error {
    color: #fca5a5;
  }
</style>
